import * as yup from 'yup'
import { parseGradeAndProgram } from 'utils/lmsHelpers'
import {
  getProductFromProductMap,
  isK8Grade,
  getSchoolOption,
} from 'utils/productHelpers'
import { PROGRAM_CODE_TO_KEY_MAP } from 'utils/productMaps'

// Common constants
export const specialCharactersMessaging = `Names can only include letters, numbers, and the following: '"-_/()#&*:,.`
export const BASICINPUTSCHEMA = yup
  .string()
  .trim()
  .matches(/^[\w\-\sáéíñóúüýÁÉÍÑÓÚÜÝ'"-–/()#&*:,.""]+$/, {
    message: specialCharactersMessaging,
    excludeEmptyString: true,
  })
  .required('This field is required')

// Shared URL parsing functions
export const getCurrentProductFromUrl = (pathName = '') => {
  // [LEARN-8666] TODO: if we cant find anything from the parsed name, default to middle school
  const defaultParsedProduct = 'grades-6-8'
  const parsedPathname = pathName.split('/')[2] || defaultParsedProduct

  return getProductFromProductMap(parsedPathname)
}

export const getCurrentProductFilter = (pathName = '') => {
  // [LEARN-8666] TODO: if we cant find anything from the parsed name, default to middle school
  const defaultParsedProduct = 'grades-6-8'
  const parsedPathname = pathName.split('/')[2] || defaultParsedProduct
  return parsedPathname
}

export const getFilterTypeFromPathname = (pathName = '') => {
  return getCurrentProductFromUrl(pathName)?.skuCode
}

/**
 * Maps program code (K5P, MSP) to program key (k5SelDigital, mspDigital)
 * Uses shared PROGRAM_CODE_TO_KEY_MAP from productMaps
 * @param {string} programCode - Program code ('K5P' or 'MSP')
 * @returns {string} - Program key ('k5SelDigital' or 'mspDigital')
 */
export const getProgramKeyFromProgramCode = programCode => {
  return PROGRAM_CODE_TO_KEY_MAP[programCode] || null
}

export const getLessonsLink = (pathName = '') => {
  const currentProductName = getCurrentProductFromUrl(pathName)?.productName

  return currentProductName
    ? `/product/${currentProductName}/lessons`
    : undefined
}

// Shared filtering functions
export const filterK8Grades = (grades = []) => {
  return grades.filter(grade => isK8Grade(grade))
}

// Shared sorting functions
export const sortGradesWithKFirst = (grades = []) => {
  return grades.sort((grade1, grade2) => {
    // Check if either grade is Grade K or Kindergarten
    const isGrade1K =
      grade1.toLowerCase().includes('grade k') ||
      grade1.toLowerCase().includes('kindergarten')
    const isGrade2K =
      grade2.toLowerCase().includes('grade k') ||
      grade2.toLowerCase().includes('kindergarten')

    if (isGrade1K && !isGrade2K) {
      return -1 // Grade K comes first
    }
    if (!isGrade1K && isGrade2K) {
      return 1 // Grade K comes first
    }

    // For non-K grades, sort alphabetically
    return grade1.localeCompare(grade2)
  })
}

export const sortCurriculumTypesWithKFirst = (curriculumTypes = []) => {
  const k2Index = curriculumTypes.findIndex(
    type => type === 'k-2_curriculum_tier2',
  )
  if (k2Index !== -1) {
    const reorderedTypes = [...curriculumTypes]
    const k2Type = reorderedTypes.splice(k2Index, 1)[0]
    reorderedTypes.unshift(k2Type)
    return reorderedTypes
  }
  return curriculumTypes
}

// Schema factory function
export const createSchema = config => {
  return yup.object().shape({
    courseInstanceName: BASICINPUTSCHEMA,
    selectedCourse: yup.string().required(),
    selectedSiteId: config.requiresSite
      ? yup.mixed().required()
      : yup.mixed().optional(),
  })
}

// Base validator class
export class BaseValidator {
  constructor(state, props, config) {
    this.state = state
    this.props = props
    this.config = config
  }

  async schema() {
    const { selectedCourse, selectedSchool, courseInstanceName } =
      this.state || {}
    // Use siteId as primary identifier (fallback to id for backward compatibility)
    const selectedSiteId = selectedSchool?.siteId || selectedSchool?.id

    let selectedCourseValue
    if (this.config.validationType === 'course') {
      selectedCourseValue = selectedCourse?.name
    } else if (this.config.validationType === 'smallGroup') {
      // For small groups, if no grade is selected but school is selected, use a placeholder
      // This allows validation to pass with just school + class name
      selectedCourseValue =
        selectedCourse || (selectedSchool ? 'placeholder' : undefined)
    }

    const course = {
      selectedSiteId,
      selectedCourse: selectedCourseValue,
      courseInstanceName,
    }

    const schema = createSchema(this.config)
    return schema.isValid(course)
  }

  async className(modalType) {
    let isValid = modalType === 'Create' ? await this.schema() : true

    const { courseInstanceName, selectedSchool } = this.state
    const courseInstanceNameTrimmed = courseInstanceName?.trim()
    const { lms, pathName } = this.props
    const { program } = parseGradeAndProgram(pathName)

    // Get the selected site ID (site-centric approach)
    const selectedSiteId = selectedSchool?.siteId

    // Filter siteEducator by selected site to only check for duplicates within that site
    const allSiteEducatorClasses =
      lms.instructorData?.[program]?.siteEducator || []
    const classesForSelectedSite = selectedSiteId
      ? allSiteEducatorClasses.filter(
          classItem => classItem?.site_id === selectedSiteId,
        )
      : allSiteEducatorClasses // Fallback: check all if no site selected

    const isDuplicateCourseName =
      classesForSelectedSite
        ?.map(x => x.class_title)
        ?.includes(courseInstanceNameTrimmed) || false

    let classNameErrors = { errors: null }

    if (courseInstanceNameTrimmed && courseInstanceNameTrimmed.length > 0) {
      try {
        await BASICINPUTSCHEMA.validate(courseInstanceNameTrimmed)
      } catch (e) {
        classNameErrors = e
        isValid = false
      }
    } else {
      isValid = false
    }

    if (isDuplicateCourseName) {
      classNameErrors = {
        errors: [this.config.duplicateMessage],
      }
      isValid = false
    }

    return {
      isFormValid: isValid,
      classNameErrors,
    }
  }
}

// Shared helper functions
/**
 * Gets the initial selected school for modal initialization
 * Only used in CreateCourse/CreateSmallGroup modal flow context
 * @param {Object} props - Component props containing all necessary data
 * @param {Object} filterConfig - Optional filter config (for small groups)
 * @returns {Object|null} - School option object or null
 */
export const getInitialSelectedSchool = (props, filterConfig = {}) => {
  try {
    const {
      currentSite,
      allSitesWithGradesForClasses,
      allSitesWithGradesForSmallGroups,
    } = props || {}

    if (!currentSite) {
      const fallbackOption = getSchoolOption({
        siteId: null,
        product: null,
        siteName: null,
        grades: [],
      })
      return {
        ...fallbackOption,
        siteId: null, // Ensure siteId is present for consistency
      }
    }

    // Use explicit flag instead of fragile function name matching
    const isSmallGroup = filterConfig?.isSmallGroup === true

    // Get sites with grades from appropriate selector
    const sitesWithGrades = isSmallGroup
      ? allSitesWithGradesForSmallGroups || []
      : allSitesWithGradesForClasses || []

    if (!sitesWithGrades || sitesWithGrades.length === 0) {
      const fallbackOption = getSchoolOption({
        siteId: null,
        product: null,
        siteName: null,
        grades: [],
      })
      return {
        ...fallbackOption,
        siteId: null, // Ensure siteId is present for consistency
      }
    }

    // Find the site matching currentSite
    const siteOption = sitesWithGrades.find(
      site => site.siteId === currentSite.siteId,
    )

    if (siteOption) {
      // Create school option from site option
      const schoolFromCurrentSite = getSchoolOption({
        siteId: siteOption.id,
        product: null,
        siteName: siteOption.siteName,
      })
      return {
        ...schoolFromCurrentSite,
        siteId: siteOption.siteId,
        grades: siteOption.grades,
        id: siteOption.id,
      }
    }

    // Fallback
    const fallbackOption = getSchoolOption({
      siteId: null,
      product: null,
      siteName: null,
      grades: [],
    })
    return {
      ...fallbackOption,
      siteId: null, // Ensure siteId is present for consistency
    }
  } catch (error) {
    console.error('Error in getInitialSelectedSchool:', error)
    // Return a safe fallback
    return {
      product: null,
      siteName: null,
      grades: [],
      siteId: null,
    }
  }
}

// Shared validation function
export const checkBlockingValidations = async (state, props, config) => {
  const { modalContent } = state || {}

  const validator = new BaseValidator(state, props, config)

  const {
    isFormValid: classNameIsValid,
    classNameErrors,
  } = await validator.className(modalContent)

  let inputErrors = {
    className: classNameErrors,
  }

  let isCreateInValid = !classNameIsValid

  // For small groups, validate that either school or grade is selected
  const { selectedSchool, selectedGrade } = state || {}
  const hasSelectedSchool =
    selectedSchool && (selectedSchool.siteId || selectedSchool.id)
  const hasSelectedGrade = selectedGrade && selectedGrade.trim() !== ''

  isCreateInValid =
    !classNameIsValid || (!hasSelectedSchool && !hasSelectedGrade)

  return {
    isFormValid: !isCreateInValid,
    inputErrors,
  }
}

// Shared limit checking function
export const checkLimitReached = (pathName, lms, maxClasses) => {
  const currentProduct = getCurrentProductFromUrl(pathName)
  const programLmsKey = currentProduct?.lmsKeyV2
  const currentProductClasses = lms.instructorData?.[programLmsKey] ?? []
  return currentProductClasses?.length >= maxClasses
}

/**
 * Unified course filtering function for both classes and small groups
 * @param {Array} courses - Array of course objects
 * @param {boolean} isSmallGroup - Whether to filter for small groups (tier2) or classes (individual grades)
 * @returns {Array} - Filtered courses
 */
export const getRelevantCourseList = (courses = [], isSmallGroup = false) => {
  if (isSmallGroup) {
    // For small groups, filter to only tier2 curriculum courses
    return courses.filter(
      course => course && course.name && course.name.includes('tier2'),
    )
  }
  // For classes, filter OUT tier2 and training courses (only individual grades: K, 1-8)
  return courses.filter(
    course =>
      course &&
      course.name &&
      !course.name.includes('tier2') &&
      !course.name.includes('training'),
  )
}
